import React, { useRef } from "react";
import "./confetti.css";
import { readBrandColor } from "../../../utils/partnerBranding";
import { palette } from "../../../styles/design-tokens";

type Shape =
    | "star"
    | "circle"
    | "circle-sm"
    | "oval"
    | "square"
    | "rect"
    | "ribbon"
    | "curly"
    | "s-curve"
    | "dot"
    | "triangle";

interface Piece {
    id: number;
    shape: Shape;
    color: string;
    left: number;
    delay: number;
    duration: number;
    size: number;
    rotate: number;
    swayAmount: number;
    swayDir: number;
}

const buildColors = (): string[] => [
    palette["yellow-50-v2"], palette["pink-71"], palette["sky-50-v2"], readBrandColor("primary", palette["purple-51"]),
    palette["green-50"], palette["red-64"], palette["teal-41"], palette["orange-50"],
    palette["pink-54"], palette["lime-59"], palette["orange-53"], readBrandColor("primary-light-2", palette["purple-65"]),
    palette["red-50"], palette["sky-43"], palette["lime-44"],
];

const SHAPES: Shape[] = [
    "star", "star", "star", "star",
    "rect", "rect", "rect",
    "ribbon", "ribbon",
    "circle", "circle-sm",
    "square", "square",
    "triangle",
    "oval",
    "dot",
    "curly",
    "s-curve", "s-curve", "s-curve",
];

function randomBetween(min: number, max: number) {
    return Math.random() * (max - min) + min;
}

function generatePieces(count: number): Piece[] {
    const colors = buildColors();
    return Array.from({ length: count }, (_, i) => {
        const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
        let size: number;
        if (shape === "star") {
            size = Math.random() < 0.3
                ? randomBetween(16, 24)
                : randomBetween(5, 11);
        } else if (shape === "circle-sm" || shape === "dot") {
            size = randomBetween(3, 7);
        } else if (shape === "ribbon") {
            size = randomBetween(3, 7);
        } else {
            size = randomBetween(4, 11);
        }
        return {
            id: i,
            shape,
            color: colors[Math.floor(Math.random() * colors.length)],
            left: randomBetween(0, 100),
            delay: randomBetween(0, 4),
            duration: randomBetween(4, 7),
            size,
            rotate: randomBetween(0, 360),
            swayAmount: randomBetween(20, 80),
            swayDir: Math.random() > 0.5 ? 1 : -1,
        };
    });
}

const StarSVG = ({ color, size }: { color: string; size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
);

const TriangleSVG = ({ color, size }: { color: string; size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <polygon points="12,3 22,21 2,21" />
    </svg>
);

const CurlySVG = ({ color, size }: { color: string; size: number }) => (
    <svg width={size * 2} height={size * 1.2} viewBox="0 0 40 20" fill="none">
        <path
            d="M2 10 Q10 2 20 10 Q30 18 38 10"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
        />
    </svg>
);

const SCurveSVG = ({ color, size }: { color: string; size: number }) => (
    <svg width={size * 1.4} height={size * 2.2} viewBox="0 0 28 44" fill="none">
        <path
            d="M20 4 C28 4 28 14 14 22 C0 30 0 40 8 40"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
        />
    </svg>
);

const ConfettiPiece = ({ piece }: { piece: Piece }) => {
    const style: React.CSSProperties = {
        left: `${piece.left}%`,
        animationDelay: `${piece.delay}s`,
        animationDuration: `${piece.duration}s`,
        "--sway": `${piece.swayAmount * piece.swayDir}px`,
    } as React.CSSProperties;

    return (
        <div className="confetti-piece" style={style}>
            <div
                className="confetti-inner"
                style={{
                    transform: `rotate(${piece.rotate}deg)`,
                    animationDuration: `${piece.duration * 0.6}s`,
                }}
            >
                {piece.shape === "star" && (
                    <StarSVG color={piece.color} size={piece.size} />
                )}
                {piece.shape === "triangle" && (
                    <TriangleSVG color={piece.color} size={piece.size} />
                )}
                {piece.shape === "circle" && (
                    <div
                        style={{
                            width: piece.size,
                            height: piece.size,
                            borderRadius: "50%",
                            backgroundColor: piece.color,
                        }}
                    />
                )}
                {piece.shape === "circle-sm" && (
                    <div
                        style={{
                            width: piece.size,
                            height: piece.size,
                            borderRadius: "50%",
                            backgroundColor: piece.color,
                        }}
                    />
                )}
                {piece.shape === "dot" && (
                    <div
                        style={{
                            width: Math.max(2, piece.size * 0.5),
                            height: Math.max(2, piece.size * 0.5),
                            borderRadius: "50%",
                            backgroundColor: piece.color,
                        }}
                    />
                )}
                {piece.shape === "oval" && (
                    <div
                        style={{
                            width: piece.size * 1.2,
                            height: piece.size * 0.55,
                            borderRadius: "50%",
                            backgroundColor: piece.color,
                        }}
                    />
                )}
                {piece.shape === "square" && (
                    <div
                        style={{
                            width: piece.size * 0.7,
                            height: piece.size * 0.7,
                            backgroundColor: piece.color,
                            borderRadius: 1,
                        }}
                    />
                )}
                {piece.shape === "rect" && (
                    <div
                        style={{
                            width: 2,
                            height: piece.size * 2.2,
                            backgroundColor: piece.color,
                            borderRadius: 1,
                        }}
                    />
                )}
                {piece.shape === "ribbon" && (
                    <div
                        style={{
                            width: Math.max(1, piece.size * 0.2),
                            height: piece.size * 4,
                            backgroundColor: piece.color,
                            borderRadius: 3,
                        }}
                    />
                )}
                {piece.shape === "curly" && (
                    <CurlySVG color={piece.color} size={piece.size} />
                )}
                {piece.shape === "s-curve" && (
                    <SCurveSVG color={piece.color} size={piece.size} />
                )}
            </div>
        </div>
    );
};

interface ConfettiProps {
    active: boolean;
    count?: number;
}

export default function Confetti({ active, count = 200 }: ConfettiProps) {
    const piecesRef = useRef<Piece[]>(generatePieces(count));

    if (!active) return null;

    return (
        <div className="confetti-container" aria-hidden="true">
            {piecesRef.current.map((piece) => (
                <ConfettiPiece key={piece.id} piece={piece} />
            ))}
        </div>
    );
}
